import {
	Component,
	computed,
	effect,
	inject,
	input,
	Signal,
	signal,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { DynamicModule } from 'ng-dynamic-component';
import { take } from 'rxjs';

import { Icon } from '@valk-nx/components/ui-icon-list/src/lib/icon-list.interface';
import { CtaType } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.interface';
import {
	fallbackLanguage,
	PRICING_TOOLTIP_OCCUPANCY,
} from '@valk-nx/core/lib/core';
import { StoryblokSearchableListSectionComponent } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section';
import { ExtendedPersuadeCardInputs } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section.interface';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { buildEventIconList } from '@valk-nx/storyblok-helpers/src/lib/general/icon-list.helper';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import {
	ThemeEventData,
	ThemePackageData,
} from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import { ISbStoryBlok } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import { StoryblokPackagesEventsListPluginData } from './packages-events-list.interface';

@Component({
	selector: 'sb-packages-events-list',
	templateUrl: './packages-events-list.html',
	imports: [DynamicModule, StoryblokSearchableListSectionComponent],
})
export class StoryblokPackagesEventsListComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly translate = inject(TranslateService);

	title = input.required<ISbStoryBlok[]>();
	filters = input.required<StoryblokPackagesEventsListPluginData>();
	hideFilter = input<boolean>(false);
	emptyListTitle = input.required<string>();
	emptyListContent = input.required<string>();

	packageEventData = signal<
		ThemePackageData[] | ThemeEventData[] | undefined
	>([]);

	packageEventItems: Signal<ExtendedPersuadeCardInputs[] | null> =
		signal(null);
	language: string = fallbackLanguage;

	constructor() {
		super();

		this.language =
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang();

		this.packageEventItems = computed(() => {
			if (this.packageEventData().length === 0) return null;

			const mappedPackageEventItems = this.packageEventData()?.map(
				(item: ThemePackageData | ThemeEventData) => {
					let iconList: Icon[] = [];

					if ('schedule' in item && item.schedule) {
						iconList = buildEventIconList(item, {
							translateFn: (key: string) =>
								this.translate.instant(key),
						});
					} else if ('iconList' in item && item.iconList) {
						iconList = item.iconList;
					}

					const cardContent =
						'content' in item
							? item.content.value
							: item.bulletContent;

					return {
						images: item.images,
						title: item.name,
						overline: item.hotelName,
						price: item.price,
						pricingToolTip: {
							occupancy: PRICING_TOOLTIP_OCCUPANCY,
						},
						content: cardContent,
						ctaType: 'btn-primary' as CtaType,
						ctaUrl: item.toUrl,
						ctaText: this.translate.instant(
							'storyblok.package-event.cta',
						),
						ctaTarget: '_blank' as const,
						showDisabledText: false,
						iconList: iconList,
					};
				},
			);

			return mappedPackageEventItems;
		});

		effect(() => {
			this.filters();
			this.runEffect();
		});
	}

	runEffect = () => {
		const filterData = this.filters();
		const themeGuids = filterData.themes.map((theme) => theme.guid);
		const hotelGuids = filterData.hotels.map((hotel) => hotel.guid);
		const themeType = filterData.themeType;

		if (themeType === 'events') {
			this.contentService
				.getThemeEvents(themeGuids, hotelGuids, this.language)
				.pipe(take(1))
				.subscribe((response: { data: ThemeEventData[] }) => {
					this.packageEventData.set(response.data);
				});
		} else {
			this.contentService
				.getThemePackages(themeGuids, hotelGuids, this.language)
				.pipe(take(1))
				.subscribe(
					(response: {
						data: { packageBatches: ThemePackageData[] };
					}) => {
						this.packageEventData.set(response.data.packageBatches);
					},
				);
		}
	};
}
